import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Box,
  Grid,
  Image,
  Text,
  Button,
  HStack,
  IconButton,
  useColorModeValue,
  Flex,
  VStack,
  Divider,
  CloseButton,
  ModalFooter,
  Input,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useDisclosure
} from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronRightIcon, CheckCircleIcon, EditIcon, LockIcon, DeleteIcon } from '@chakra-ui/icons';
import { updateUserEquipment, uploadUserEquipmentImage } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useSwipeable } from 'react-swipeable';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';

export default function SelectedEquipmentModal({ item, config, onClose, onPreferenceChange, onUserEquipmentChange, isTsubo, equipmentDefinitions, fixedMap, targetUserId, navigate, onEquipmentTypeChange, isReadOnly = false }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const { currentUser, isFunctionEnabled } = useAuth();
  const isAdmin = targetUserId && targetUserId !== currentUser?.uid;
  const [editMode, setEditMode] = useState(false);
  const [editValues, setEditValues] = useState({});
  const [uploading, setUploading] = useState(false);
  const [images, setImages] = useState([]);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [currentIframeSrc, setCurrentIframeSrc] = useState('');
  const [imageDescription, setImageDescription] = useState('');
  
  // 確認ダイアログ用のstate
  const { isOpen: isConfirmDialogOpen, onOpen: openConfirmDialog, onClose: closeConfirmDialog } = useDisclosure();
  const [confirmDialogMessage, setConfirmDialogMessage] = useState('');
  const [confirmDialogAction, setConfirmDialogAction] = useState(null);
  const confirmDialogCancelRef = React.useRef();

  // 拡大モーダル用のスワイプ機能の設定
  const modalSwipeHandlers = useSwipeable({
    onSwipedLeft: () => {
      if (imageUrls.length > 1) {
        const newIndex = currentIndex < imageUrls.length - 1 ? currentIndex + 1 : 0;
        setCurrentIndex(newIndex);
      }
    },
    onSwipedRight: () => {
      if (imageUrls.length > 1) {
        const newIndex = currentIndex > 0 ? currentIndex - 1 : imageUrls.length - 1;
        setCurrentIndex(newIndex);
      }
    },
    preventDefaultTouchmoveEvent: true,
    trackMouse: false
  });

  // 通常の画像セクション用のスワイプ機能の設定
  const imageSwipeHandlers = useSwipeable({
    onSwipedLeft: () => {
      if (imageUrls.length > 1) {
        const newIndex = currentIndex < imageUrls.length - 1 ? currentIndex + 1 : 0;
        setCurrentIndex(newIndex);
      }
    },
    onSwipedRight: () => {
      if (imageUrls.length > 1) {
        const newIndex = currentIndex > 0 ? currentIndex - 1 : imageUrls.length - 1;
        setCurrentIndex(newIndex);
      }
    },
    preventDefaultTouchmoveEvent: true,
    trackMouse: false
  });

  // 未選択の設備種別を取得する関数
  const getUnselectedEquipmentTypes = useCallback(() => {
    if (!equipmentDefinitions?.equipment_types) return [];
    
    const sortedEquipmentTypes = Object.entries(equipmentDefinitions.equipment_types)
      .sort((a, b) => (a[1].display_order || 0) - (b[1].display_order || 0))
      .filter(([key, definition]) => definition.enabled);
    
    return sortedEquipmentTypes.filter(([key]) => !fixedMap[key]);
  }, [equipmentDefinitions, fixedMap]);

  // 次の未選択設備種別を取得する関数
  const getNextUnselectedEquipmentType = useCallback(() => {
    const unselectedTypes = getUnselectedEquipmentTypes();
    return unselectedTypes.length > 0 ? unselectedTypes[0][0] : null;
  }, [getUnselectedEquipmentTypes]);

  // 全設備が選択済みかチェックする関数
  const areAllEquipmentsSelected = useCallback(() => {
    const unselectedTypes = getUnselectedEquipmentTypes();
    return unselectedTypes.length === 0;
  }, [getUnselectedEquipmentTypes]);

  // 確認ダイアログを表示する関数
  const showConfirmDialog = useCallback((message, action) => {
    setConfirmDialogMessage(message);
    setConfirmDialogAction(() => action);
    openConfirmDialog();
  }, [openConfirmDialog]);

  // 確認ダイアログの実行
  const handleConfirmDialogYes = useCallback(() => {
    if (confirmDialogAction) {
      confirmDialogAction();
    }
    closeConfirmDialog();
    setConfirmDialogAction(null);
  }, [confirmDialogAction, closeConfirmDialog]);

  // 確認ダイアログのキャンセル
  const handleConfirmDialogNo = useCallback(() => {
    closeConfirmDialog();
    setConfirmDialogAction(null);
  }, [closeConfirmDialog]);

  // 画像データの配列を取得（URL + 説明文）
  const imageData = useMemo(() => {
    if (!item) return [];
    
    // 新形式（images配列）をチェック
    if (item.images && item.images.length > 0) {
      return item.images.map(img => {
        if (typeof img === 'string') {
          // 旧形式（文字列）を新形式に変換
          return { url: img, description: '' };
        }
        // 新形式（オブジェクト）
        return { url: img.url || img, description: img.description || '' };
      });
    }
    
    // 旧形式（個別のURL）をチェック
    const urls = [];
    for (let i = 1; i <= 5; i++) {
      const url = item[`image${i}_sas_url`];
      if (url) urls.push({ url, description: '' });
    }
    return urls;
  }, [item]);

  // 下位互換性のため、URLのみの配列も作成
  const imageUrls = useMemo(() => {
    return imageData.map(img => img.url);
  }, [imageData]);

  useEffect(() => {
    // itemが変更されたときにstateを更新
    if (item) {
      // 画像データを適切に設定
      let imageData = [];
      if (item.images && item.images.length > 0) {
        imageData = item.images.map(img => {
          if (typeof img === 'string') {
            return { url: img, description: '' };
          }
          return { url: img.url || img, description: img.description || '' };
        });
      } else {
        // 旧形式の画像URLがある場合
        for (let i = 1; i <= 5; i++) {
          const url = item[`image${i}_sas_url`];
          if (url) {
            imageData.push({ url, description: '' });
          }
        }
      }
      
      setImages(imageData);
      setCurrentIndex(0);
      
      // ユーザ登録設備の場合、編集用のデフォルト値を設定
      if (item.is_user_equipment) {
        const defaultValues = { ...item };
        defaultValues.images = imageData;
        setEditValues(defaultValues);
      }
    }
  }, [item]);

  const goToNext = useCallback(() => {
    if (imageUrls.length > 1) {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % imageUrls.length);
    }
  }, [imageUrls.length]);

  const goToPrevious = useCallback(() => {
    if (imageUrls.length > 1) {
      setCurrentIndex((prevIndex) => (prevIndex - 1 + imageUrls.length) % imageUrls.length);
    }
  }, [imageUrls.length]);

  useEffect(() => {
    if (imageUrls.length > 1 && !isImageModalOpen) { // 拡大モーダル中は自動切り替えしない
      const timer = setTimeout(goToNext, 3000);
      return () => clearTimeout(timer);
    }
  }, [currentIndex, imageUrls.length, goToNext, isImageModalOpen]);



  if (!item) return null;

  const handleLikeClick = (e) => {
    e.stopPropagation();
    onPreferenceChange(item.id, item.item_type, { is_liked: !item.is_liked });
  };
  
  const handleFixClick = (e) => {
    e.stopPropagation();
    
    // 暫定を設定する場合の処理
    if (!item.is_fixed) {
      onPreferenceChange(item.id, item.item_type, { is_fixed: true });
      onClose();
      
      // 少し遅延してから確認ダイアログを表示
      setTimeout(() => {
        // fixedMapを更新（現在の設備種別を追加）
        const updatedFixedMap = { ...fixedMap, [item.item_type]: true };
        
        // 更新されたfixedMapを使って次の未選択設備種別を取得
        const sortedEquipmentTypes = Object.entries(equipmentDefinitions?.equipment_types || {})
          .sort((a, b) => (a[1].display_order || 0) - (b[1].display_order || 0))
          .filter(([key, definition]) => definition.enabled);
        
        const unselectedTypes = sortedEquipmentTypes.filter(([key]) => !updatedFixedMap[key]);
        const nextUnselectedType = unselectedTypes.length > 0 ? unselectedTypes[0][0] : null;
        
        if (nextUnselectedType) {
          // 次の未選択設備種別がある場合
          const nextEquipmentName = equipmentDefinitions?.equipment_types?.[nextUnselectedType]?.name || nextUnselectedType;
          showConfirmDialog(
            `${nextEquipmentName}の選択に移りますか？`,
            () => {
              // 親コンポーネントの設備種別を変更
              if (onEquipmentTypeChange) {
                onEquipmentTypeChange(nextUnselectedType);
              }
            }
          );
        } else if (unselectedTypes.length === 0) {
          // 全設備が選択完了した場合
          showConfirmDialog(
            '概算見積ページに移りますか？',
            () => {
              const estimateUrl = targetUserId 
                ? `/admin/user/${targetUserId}/equipment/confirmed-summary` 
                : '/equipment/confirmed-summary';
              navigate(estimateUrl);
            }
          );
        }
      }, 500);
    } else {
      // 暫定を解除する場合
      onPreferenceChange(item.id, item.item_type, { is_fixed: false });
    }
  };

  const handlePrevImageClick = (e) => { 
    e.stopPropagation(); 
    goToPrevious(); 
  };
  const handleNextImageClick = (e) => { 
    e.stopPropagation(); 
    goToNext(); 
  };

  // Chakra UIのカラーモードに基づく色の設定
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.800', 'white');
  const subTextColor = useColorModeValue('gray.600', 'gray.400');

  const formatPrice = (price) => {
    if (price === null || price === undefined) return '価格未定';
    return (
      <>
        <Text as="span" fontSize="lg" fontWeight="bold">¥{price.toLocaleString()}</Text>
        <Text as="span" fontSize="xs" color="gray.600"> (税抜)</Text>
      </>
    );
  };

  const handleSave = async () => {
    try {
      // 編集可能なフィールドのみ抽出
      const editableFieldNames = config.fields
        .filter(field =>
          ![
            'id', 'user_id', 'created_at', 'updated_at', 'is_liked', 'is_fixed', 'locked_at',
            'signature_url', 'memo', 'material_files', 'preferenceId', 'recommended', 'default_quantity'
          ].includes(field.name)
        )
        .map(field => field.name);

      const payload = {};
      editableFieldNames.forEach(name => {
        if (editValues.hasOwnProperty(name)) {
          payload[name] = editValues[name];
        }
      });
      payload.user_id = item.user_id;
      await updateUserEquipment(
        item.item_type,
        item.id,
        payload
      );
      setEditMode(false);
      if (onUserEquipmentChange) {
        onUserEquipmentChange();
      }
      if (onClose) {
        onClose(); // 編集完了後にモーダルを閉じる
      }
    } catch (e) {
      alert('保存に失敗しました');
    }
  };

  const handleEditFloorplan = () => {
    const url = (item.user_id && item.user_id !== currentUser.uid) ? `/admin/user/${item.user_id}/planner` : '/planner';
    navigate(url, { state: { floorRooms: item.rooms || [], targetUserId: item.user_id, mode: 'edit', user_equipment_id: item.id, images: item.images || [] } });
  };

  // 編集モードに入る際の処理
  const handleEditMode = () => {
    // 既存の値をデフォルト値として設定
    const defaultValues = { ...item };
    
    // 画像データを適切に設定
    if (item.images && item.images.length > 0) {
      defaultValues.images = item.images.map(img => {
        if (typeof img === 'string') {
          return { url: img, description: '' };
        }
        return { url: img.url || img, description: img.description || '' };
      });
    } else {
      // 旧形式の画像URLがある場合
      const oldImages = [];
      for (let i = 1; i <= 5; i++) {
        const url = item[`image${i}_sas_url`];
        if (url) {
          oldImages.push({ url, description: '' });
        }
      }
      if (oldImages.length > 0) {
        defaultValues.images = oldImages;
      }
    }
    
    setEditValues(defaultValues);
    setImages(defaultValues.images || []);
    setEditMode(true);
  };

  return (
    <Modal isOpen={true} onClose={onClose} size="xl" isCentered>
      <ModalOverlay />
      <ModalContent maxW="800px" maxH="90vh" overflowY="auto">
        <ModalHeader borderBottomWidth="1px">
          {item.manufacturer} - {item.name}
        </ModalHeader>
        <ModalCloseButton 
          size="lg"
          bg="gray.100"
          _hover={{ bg: "yellow.400" }}
          borderRadius="full"
        />
        <ModalBody py={4}>
          <Grid templateColumns={{ base: '1fr', md: imageUrls.length > 0 ? 'repeat(2, 1fr)' : '1fr' }} gap={6}>
            {/* 画像セクション - 画像がある場合のみ表示 */}
            {imageUrls.length > 0 && (
              <Box position="relative" borderWidth="1px" borderColor={borderColor} borderRadius="md" overflow="hidden" minH="300px">
                <Box {...imageSwipeHandlers}>
                  <Image
                    src={imageUrls[currentIndex]}
                    alt={`${item.name} - 画像 ${currentIndex + 1}`}
                    w="100%"
                    h="280px"
                    objectFit="contain"
                    cursor="pointer"
                    onClick={() => setIsImageModalOpen(true)}
                  />
                  {imageData[currentIndex]?.description && (
                    <Text
                      fontSize="sm"
                      color={subTextColor}
                      textAlign="center"
                      mt={2}
                      px={2}
                      bg="gray.50"
                      borderRadius="md"
                      py={1}
                    >
                      {imageData[currentIndex].description}
                    </Text>
                  )}
                </Box>
                {imageUrls.length > 1 && (
                  <>
                    <IconButton
                      icon={<ChevronLeftIcon />}
                      aria-label="前の画像"
                      position="absolute"
                      left={2}
                      top="50%"
                      transform="translateY(-50%)"
                      onClick={handlePrevImageClick}
                      colorScheme="blackAlpha"
                    />
                    <IconButton
                      icon={<ChevronRightIcon />}
                      aria-label="次の画像"
                      position="absolute"
                      right={2}
                      top="50%"
                      transform="translateY(-50%)"
                      onClick={handleNextImageClick}
                      colorScheme="blackAlpha"
                    />
                  </>
                )}
              </Box>
            )}

            {/* 詳細情報セクション */}
            <VStack align="stretch" spacing={4}>
              {item.is_user_equipment && editMode ? (
                <>
                  {config.fields && config.fields.map((field, index) => {
                    if ([
                      'id', 'user_id', 'created_at', 'updated_at', 'images', 'signature_url', 'locked_at', 'preferenceId', 'is_liked', 'is_fixed', 'memo', 'material_files', 'recommended', 'default_quantity'
                    ].includes(field.name)) {
                      return null;
                    }
                    
                    // boolean型
                    if (field.type === 'boolean') {
                      return (
                        <Box key={field.name}>
                          <Text fontSize="sm" color={subTextColor}>{field.label}</Text>
                          <input
                            type="checkbox"
                            checked={!!editValues[field.name]}
                            onChange={e => setEditValues(v => ({ ...v, [field.name]: e.target.checked }))}
                          />
                        </Box>
                      );
                    }
                    // select型
                    if (field.type === 'select') {
                      return (
                        <Box key={field.name}>
                          <Text fontSize="sm" color={subTextColor}>{field.label}</Text>
                          <select
                            value={editValues[field.name] || ''}
                            onChange={e => setEditValues(v => ({ ...v, [field.name]: e.target.value }))}
                          >
                            <option value="">選択してください</option>
                            {field.options?.map(opt => (
                              <option key={opt} value={opt}>{opt}</option>
                            ))}
                          </select>
                        </Box>
                      );
                    }
                    // number/string型
                    return (
                      <Box key={field.name}>
                        <Text fontSize="sm" color={subTextColor}>{field.label}</Text>
                        <input
                          type={field.type === 'number' ? 'number' : 'text'}
                          value={editValues[field.name] ?? ''}
                          onChange={e => setEditValues(v => ({ ...v, [field.name]: e.target.value }))}
                        />
                      </Box>
                    );
                
                  })}
                  {/* 画像アップロードボックス（ユーザ登録設備の場合のみ） */}
                  <Box mt={2}>
                    <Text fontSize="sm" color={subTextColor} mb={2}>画像をアップロード</Text>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (!file) return;
                        try {
                          setUploading(true);
                          const user = currentUser;
                          const res = await uploadUserEquipmentImage(
                            item.item_type,
                            file,
                            user,
                            item.user_id,
                            imageDescription
                          );
                          // 新形式のオブジェクトで追加
                          const newImageObj = { url: res.url, description: res.description || imageDescription };
                          setEditValues(v => ({ ...v, images: [...images, newImageObj] }));
                          setImages(v => [...v, newImageObj]);
                          setImageDescription(''); // 説明文をリセット
                        } catch (err) {
                          alert('画像のアップロードに失敗しました');
                        } finally {
                          setUploading(false);
                        }
                      }}
                      disabled={uploading}
                    />
                    {uploading && <Text fontSize="xs" color="gray.500">アップロード中...</Text>}
                    {images && (
                      <Box mt={1}>
                        <Text fontSize="xs" color="gray.600" mb={2}>
                          アップロード済み画像
                        </Text>
                        {images.map((imageItem, index) => {
                          const imageUrl = typeof imageItem === 'string' ? imageItem : imageItem.url;
                          const imageDesc = typeof imageItem === 'string' ? '' : (imageItem.description || '');
                          const imageKey = imageUrl || index;
                          return (
                            <Box key={imageKey} mb={3} p={2} border="1px solid" borderColor="gray.200" borderRadius="md">
                              <HStack spacing={3} align="start">
                                <Box position="relative" display="inline-block">
                                  <img src={imageUrl} alt={`設備画像 ${index + 1}`} style={{ maxWidth: 120, maxHeight: 120 }} />
                                  <CloseButton
                                    size="sm"
                                    position="absolute"
                                    top="2px"
                                    right="2px"
                                    color="red.500"
                                    bg="whiteAlpha.800"
                                    borderRadius="full"
                                    onClick={() => {
                                      const newImages = images.filter((_, i) => i !== index);
                                      setImages(newImages);
                                      setEditValues(v => ({ ...v, images: newImages }));
                                    }}
                                    zIndex={1}
                                  />
                                </Box>
                                <VStack align="start" flex={1}>
                                  <Text fontSize="xs" color="gray.500">画像の説明</Text>
                                  <Input
                                    size="sm"
                                    placeholder="説明を入力"
                                    value={imageDesc}
                                    onChange={(e) => {
                                      const newImages = [...images];
                                      if (typeof newImages[index] === 'string') {
                                        newImages[index] = { url: newImages[index], description: e.target.value };
                                      } else {
                                        newImages[index] = { ...newImages[index], description: e.target.value };
                                      }
                                      setImages(newImages);
                                      setEditValues(v => ({ ...v, images: newImages }));
                                    }}
                                  />
                                </VStack>
                              </HStack>
                            </Box>
                          );
                        })}
                      </Box>
                    )}
                  </Box>
                  <HStack>
                    <Button bg="black" color="white" onClick={handleSave}>保存</Button>
                    <Button onClick={() => {
                      // キャンセル時は元の値に戻す
                      if (item && item.is_user_equipment) {
                        const defaultValues = { ...item };
                        if (item.images && item.images.length > 0) {
                          defaultValues.images = item.images.map(img => {
                            if (typeof img === 'string') {
                              return { url: img, description: '' };
                            }
                            return { url: img.url || img, description: img.description || '' };
                          });
                        } else {
                          const oldImages = [];
                          for (let i = 1; i <= 5; i++) {
                            const url = item[`image${i}_sas_url`];
                            if (url) {
                              oldImages.push({ url, description: '' });
                            }
                          }
                          if (oldImages.length > 0) {
                            defaultValues.images = oldImages;
                          }
                        }
                        setEditValues(defaultValues);
                        setImages(defaultValues.images || []);
                      }
                      setEditMode(false);
                    }}>キャンセル</Button>
                  </HStack>
                </>
              ) : (
                <>
                  <Box>
                    <Text fontSize="sm" color={subTextColor}>メーカー</Text>
                    <Text fontSize="lg" fontWeight="bold">{item.manufacturer}</Text>
                  </Box>
                  <Box>
                    <Text fontSize="sm" color={subTextColor}>商品名</Text>
                    <Text fontSize="lg" fontWeight="bold">{item.name}</Text>
                  </Box>
                  <Box>
                    <Text fontSize="sm" color={subTextColor}>価格</Text>
                    <Box fontSize="lg" fontWeight="bold">
                      {formatPrice(item.price)}
                    </Box>
                  </Box>
                  {item.features && (
                    <Box>
                      <Text fontSize="sm" color={subTextColor}>特徴</Text>
                      <Text>{item.features}</Text>
                    </Box>
                  )}
                  {config.fields && Object.entries(config.fields).map(([fieldName, field]) => {
                    const value = item[fieldName];
                    if (value === undefined || value === null || fieldName=='price' || fieldName=='features' || fieldName=='recommended' || fieldName=='manufacturer' || fieldName=='name' || fieldName=='images' || fieldName=='default_quantity') return null;
                    // floorplanのm2のみ単位切替
                    if (config.key === 'floorplan' && field.unit === 'm2') {
                      return (
                        <Box key={fieldName}>
                          <Text fontSize="sm" color={subTextColor}>{field.label}{isTsubo ? '（坪）' : '（m²）'}</Text>
                          <Text>{isTsubo ? value*0.3025 + '坪' : value + 'm²'}</Text>
                        </Box>
                      );
                    }
                    return (
                      <Box key={fieldName}>
                        <Text fontSize="sm" color={subTextColor}>{field.label}</Text>
                        <Text>{typeof value === 'boolean' ? (value ? 'あり' : 'なし') : value}{field.unit || ''}</Text>
                      </Box>
                    );
                  })}
                  
                  <Divider />
                  {!isReadOnly && item.is_user_equipment && (
                    <Button colorScheme="gray" onClick={handleEditMode} disabled={isFunctionEnabled('lock') ? !!item.locked_at : false}>{config.key==='floorplan' ? '情報を編集' : '編集'}</Button>
                  )}
                  {!isReadOnly && item.is_user_equipment && item.item_type === 'floorplan' && !editMode && isFunctionEnabled('floor_plan') && (
                    <Button bg="black" color="white" onClick={handleEditFloorplan} mb={2}>間取りを編集</Button>
                  )}
                  {item.external_link_url && (
                    <Button bg="black" color="white" onClick={() => window.open(item.external_link_url, '_blank')}>{item.external_link_name}</Button>
                  )}

                  {!isReadOnly && (
                    <HStack spacing={4}>
                      <Button
                        leftIcon={item.is_liked ? '❤️' : '🤍'}
                        colorScheme="pink"
                        variant={item.is_liked ? "solid" : "outline"}
                        onClick={handleLikeClick}
                      >
                        {item.is_liked ? "お気に入り解除" : "お気に入り"}
                      </Button>
                      <Button
                        leftIcon={item.is_fixed ? <CheckCircleIcon /> : <CheckCircleIcon color="gray.400" />}
                        colorScheme={item.is_fixed ? "yellow" : "gray"}
                        variant={item.is_fixed ? "solid" : "outline"}
                        onClick={handleFixClick}
                        isDisabled={isFunctionEnabled('lock') ? item.locked_at : false}
                      >
                        {item.is_fixed ? "暫定解除" : "暫定"}
                      </Button>
                    </HStack>
                  )}

                  {/* アクションセクションは削除 */}
                </>
              )}
            </VStack>
          </Grid>
        </ModalBody>
        <ModalFooter borderTopWidth="1px">
          <Button onClick={onClose} mr={3}>閉じる</Button>
        </ModalFooter>
      </ModalContent>

      {/* 画像拡大表示用のモーダル */}
      <Modal isOpen={isImageModalOpen} onClose={() => setIsImageModalOpen(false)} size="full" isCentered>
        <ModalOverlay bg="blackAlpha.800" />
        <ModalContent bg="transparent" boxShadow="none" w="100vw" h="100vh" m={0}>
          <ModalCloseButton 
            color="white" 
            bg="blackAlpha.600" 
            _hover={{ bg: "blackAlpha.800" }}
            size={{ base: "md", md: "lg" }}
            zIndex={20}
            position="fixed"
            top={{ base: 2, md: 4 }}
            right={{ base: 2, md: 4 }}
          />
          <ModalBody p={0} display="flex" alignItems="center" justifyContent="center" w="100%" h="100%">
            {imageUrls.length > 1 && (
              <>
                <IconButton
                  icon={<ChevronLeftIcon />}
                  aria-label="前の画像"
                  position="absolute"
                  left={2}
                  top="50%"
                  transform="translateY(-50%)"
                  onClick={handlePrevImageClick}
                  bg="whiteAlpha.800"
                  color="black"
                  _hover={{ bg: 'yellow.400', color: 'black' }}
                  zIndex={10}
                />
                <IconButton
                  icon={<ChevronRightIcon />}
                  aria-label="次の画像"
                  position="absolute"
                  right={2}
                  top="50%"
                  transform="translateY(-50%)"
                  onClick={handleNextImageClick}
                  bg="whiteAlpha.800"
                  color="black"
                  _hover={{ bg: 'yellow.400', color: 'black' }}
                  zIndex={10}
                />
              </>
            )}
            <TransformWrapper
              initialScale={1}
              minScale={0.5}
              maxScale={4}
              centerOnInit={true}
              limitToBounds={false}
              smooth={true}
              wheel={{
                step: 0.1,
                wheelEnabled: true,
                touchPadEnabled: true,
              }}
              pinch={{
                step: 1,
                disabled: false,
              }}
              doubleClick={{
                step: 2,
                mode: "toggle",
              }}
                              onTransformed={(state) => {
                  // ズーム状態を追跡
                }}
            >
              {({ zoomIn, zoomOut, resetTransform, state }) => (
                <TransformComponent
                  wrapperStyle={{
                    width: "100%",
                    height: "100vh",
                    maxWidth: "100%",
                    maxHeight: "100%",
                    touchAction: "none",
                  }}
                  contentStyle={{
                    width: "100%",
                    height: "100%",
                    touchAction: "none",
                  }}
                >
                  <Box position="relative" w="100%" h="100%">
                    <Image
                      src={imageUrls[currentIndex]}
                      alt={`${item.name} - 画像 ${currentIndex + 1}`}
                      w="100%"
                      h="100vh"
                      objectFit="contain"
                      draggable={false}
                      userSelect="none"
                      style={{
                        touchAction: "none",
                        WebkitTouchCallout: "none",
                        WebkitUserSelect: "none",
                        KhtmlUserSelect: "none",
                        MozUserSelect: "none",
                        msUserSelect: "none",
                        userSelect: "none",
                      }}
                    />
                    {imageData[currentIndex]?.description && (
                      <Text
                        fontSize="lg"
                        color="white"
                        textAlign="center"
                        bg="blackAlpha.600"
                        px={4}
                        py={2}
                        borderRadius="md"
                        maxW="80%"
                        position="absolute"
                        bottom={{ base: 16, md: 4 }}
                        left="50%"
                        transform="translateX(-50%)"
                        zIndex={5}
                      >
                        {imageData[currentIndex].description}
                      </Text>
                    )}
                    {/* ズームインジケーター */}
                    {state && state.scale !== 1 && (
                      <Box
                        position="absolute"
                        top={4}
                        left="50%"
                        transform="translateX(-50%)"
                        bg="blackAlpha.600"
                        color="white"
                        px={3}
                        py={1}
                        borderRadius="md"
                        fontSize="sm"
                        zIndex={10}
                      >
                        ズーム: {Math.round(state.scale * 100)}%
                      </Box>
                    )}
                    {/* ズームコントロールボタン */}
                    <HStack
                      position="absolute"
                      bottom={4}
                      right={4}
                      spacing={2}
                      zIndex={10}
                    >
                      <IconButton
                        icon={<Text fontSize="sm">+</Text>}
                        onClick={() => zoomIn()}
                        bg="blackAlpha.600"
                        color="white"
                        _hover={{ bg: "blackAlpha.800" }}
                        size="sm"
                        aria-label="ズームイン"
                      />
                      <IconButton
                        icon={<Text fontSize="sm">-</Text>}
                        onClick={() => zoomOut()}
                        bg="blackAlpha.600"
                        color="white"
                        _hover={{ bg: "blackAlpha.800" }}
                        size="sm"
                        aria-label="ズームアウト"
                      />
                      <IconButton
                        icon={<Text fontSize="sm">⟲</Text>}
                        onClick={() => resetTransform()}
                        bg="blackAlpha.600"
                        color="white"
                        _hover={{ bg: "blackAlpha.800" }}
                        size="sm"
                        aria-label="リセット"
                      />
                    </HStack>
                  </Box>
                </TransformComponent>
              )}
            </TransformWrapper>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* 確認ダイアログ */}
      <AlertDialog
        isOpen={isConfirmDialogOpen}
        leastDestructiveRef={confirmDialogCancelRef}
        onClose={handleConfirmDialogNo}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              確認
            </AlertDialogHeader>

            <AlertDialogBody>
              {confirmDialogMessage}
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={confirmDialogCancelRef} onClick={handleConfirmDialogNo}>
                いいえ
              </Button>
              <Button colorScheme="gray" bg="black" color="white" onClick={handleConfirmDialogYes} ml={3}>
                はい
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Modal>
  );
} 